import { useState, type ReactNode } from "react";
import { Bouton } from "@/composants/ui/bouton";
import { Champ, ChampSelection, ChampTexte, MentionObligatoire } from "@/composants/ui/champ";
import { useReferentiel } from "@/lib/referentiels";
import type { Fournisseur } from "@/lib/api";

const VIDE = {
  nom: "",
  siren: "",
  siret: "",
  adresse_ligne1: "",
  code_postal: "",
  ville: "",
  email: "",
  telephone: "",
  delai_moyen_jours: "",
  categorie: "",
  notes_generales: "",
  notes_confidentielles: "",
};

export type Valeurs = typeof VIDE;

function depuis(fournisseur: Fournisseur | undefined): Valeurs {
  if (!fournisseur) return VIDE;
  return {
    nom: fournisseur.nom,
    siren: fournisseur.siren ?? "",
    siret: fournisseur.siret ?? "",
    adresse_ligne1: fournisseur.adresse_ligne1 ?? "",
    code_postal: fournisseur.code_postal ?? "",
    ville: fournisseur.ville ?? "",
    email: fournisseur.email ?? "",
    telephone: fournisseur.telephone ?? "",
    delai_moyen_jours: fournisseur.delai_moyen_jours?.toString() ?? "",
    categorie: fournisseur.categorie ?? "",
    notes_generales: fournisseur.notes_generales ?? "",
    notes_confidentielles: fournisseur.notes_confidentielles ?? "",
  };
}

/**
 * La fiche fournisseur, à créer ou à corriger.
 *
 * Le formulaire ne vérifie ni la forme ni l'unicité d'un SIRET : la base le
 * fait, et son refus s'affiche tel qu'elle l'écrit (arbitrage n°3). Un SIRET
 * se tape avec ses espaces ; le serveur les retire.
 *
 * La catégorie est facultative et se prend parmi les valeurs actives du
 * référentiel (ADR-0030). Une catégorie désactivée depuis reste proposée à la
 * fiche qui la porte, pour qu'une correction ne la fasse pas tomber.
 *
 * `avecNotesConfidentielles` : les notes confidentielles ne sont proposées qu'à
 * qui les lit. Sans cela, un champ vide à l'écran écraserait des notes que l'API
 * ne lui a pas montrées, et l'API refuse d'ailleurs cette écriture.
 *
 * `initiales` : une fiche préremplie par l'annuaire des entreprises (#13), que
 * l'utilisateur corrige avant de la créer. `mention` s'affiche juste au-dessus
 * des boutons : là où l'œil se trouve au moment de valider.
 */
export function FormulaireFournisseur({
  fournisseur,
  initiales,
  avecNotesConfidentielles,
  libelleValider,
  mention,
  onValider,
  onAnnuler,
}: {
  fournisseur?: Fournisseur;
  initiales?: Partial<Valeurs>;
  avecNotesConfidentielles: boolean;
  libelleValider: string;
  mention?: ReactNode;
  onValider: (champs: Record<string, unknown>) => Promise<void>;
  onAnnuler: () => void;
}) {
  const [valeurs, setValeurs] = useState(() => ({ ...depuis(fournisseur), ...initiales }));
  const categories = useOptionsCategorie(fournisseur);
  const [envoi, setEnvoi] = useState(false);
  const poser = (champ: keyof Valeurs) => (valeur: string) =>
    setValeurs({ ...valeurs, [champ]: valeur });

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={async (evenement) => {
        evenement.preventDefault();
        setEnvoi(true);
        try {
          // Une chaîne vide efface le champ : `null` est ce que la base attend.
          const champs = Object.entries(valeurs)
            .filter(([cle]) => avecNotesConfidentielles || cle !== "notes_confidentielles")
            .map(([cle, valeur]) => {
              if (valeur.trim() === "") return [cle, null];
              return [cle, cle === "delai_moyen_jours" ? Number(valeur) : valeur];
            });
          await onValider(Object.fromEntries(champs));
        } finally {
          setEnvoi(false);
        }
      }}
    >
      <MentionObligatoire />
      <div className="grid gap-4 sm:grid-cols-2">
        <Champ
          libelle="Raison sociale"
          className="sm:col-span-2"
          required
          value={valeurs.nom}
          onChange={(e) => poser("nom")(e.target.value)}
        />
        <Champ
          libelle="SIREN"
          inputMode="numeric"
          aide="Neuf chiffres : l'entreprise."
          value={valeurs.siren}
          onChange={(e) => poser("siren")(e.target.value)}
        />
        <Champ
          libelle="SIRET"
          inputMode="numeric"
          aide="Quatorze chiffres : l'établissement qui reçoit vos commandes."
          value={valeurs.siret}
          onChange={(e) => poser("siret")(e.target.value)}
        />
        <Champ
          libelle="Adresse"
          className="sm:col-span-2"
          value={valeurs.adresse_ligne1}
          onChange={(e) => poser("adresse_ligne1")(e.target.value)}
        />
        <Champ
          libelle="Code postal"
          value={valeurs.code_postal}
          onChange={(e) => poser("code_postal")(e.target.value)}
        />
        <Champ libelle="Ville" value={valeurs.ville} onChange={(e) => poser("ville")(e.target.value)} />
        <Champ
          libelle="Adresse électronique"
          type="email"
          value={valeurs.email}
          onChange={(e) => poser("email")(e.target.value)}
        />
        <Champ
          libelle="Téléphone"
          type="tel"
          value={valeurs.telephone}
          onChange={(e) => poser("telephone")(e.target.value)}
        />
        <Champ
          libelle="Délai moyen (jours)"
          type="number"
          min={0}
          aide="Il proposera la date prévue de vos commandes chez ce fournisseur."
          value={valeurs.delai_moyen_jours}
          onChange={(e) => poser("delai_moyen_jours")(e.target.value)}
        />
        <ChampSelection
          libelle="Catégorie"
          options={categories}
          value={valeurs.categorie}
          onChange={(e) => poser("categorie")(e.target.value)}
        />
        <ChampTexte
          libelle="Notes générales"
          className="sm:col-span-2"
          value={valeurs.notes_generales}
          onChange={(e) => poser("notes_generales")(e.target.value)}
        />
        {avecNotesConfidentielles ? (
          <ChampTexte
            libelle="Notes confidentielles"
            className="sm:col-span-2"
            value={valeurs.notes_confidentielles}
            onChange={(e) => poser("notes_confidentielles")(e.target.value)}
          />
        ) : null}
      </div>
      {mention}
      <div className="flex flex-wrap gap-2">
        <Bouton type="submit" variante="principal" disabled={envoi}>
          {envoi ? "Enregistrement…" : libelleValider}
        </Bouton>
        <Bouton type="button" variante="discret" onClick={onAnnuler} disabled={envoi}>
          Annuler
        </Bouton>
      </div>
    </form>
  );
}

function useOptionsCategorie(fournisseur: Fournisseur | undefined) {
  const actives = useReferentiel("categories-fournisseur", { actives: true });
  const options = (actives.donnees?.valeurs ?? []).map((v) => ({
    valeur: v.code,
    libelle: v.libelle,
  }));
  const portee = fournisseur?.categorie;
  if (portee && !options.some((o) => o.valeur === portee)) {
    options.unshift({
      valeur: portee,
      libelle: `${fournisseur.categorie_libelle ?? portee} (désactivée)`,
    });
  }
  return [{ valeur: "", libelle: "— Aucune —" }, ...options];
}
